import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PlayerStatsInterface } from '../../models/PlayerStats';
import { StatsContainer, PageTitle, StatsGrid, StatsHeader, ControlsSection, ControlsToggle, ControlsPanel, SearchSection, LoadingState, ErrorState, SortControls, SortButton, FilterControls, FilterButton } from './PlayerStatsStyles';
import { TigersPlayerCard } from '../../components/TigersPlayerCard';
import { PlayerInfo } from '../../models/PlayerInfo';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
interface CombinedPlayerData extends PlayerStatsInterface {
  position: string;
  number: number;
}

type SortField =
  | 'name'
  | 'number'
  | 'gamesPlayed'
  | 'goals'
  | 'assists'
  | 'points'
  | 'ppg'
  | 'pims'
  | 'wins'
  | 'loses'
  | 'so'
  | 'ga'
  | 'gaa'
  | 'svPercent';
type SortOrder = 'asc' | 'desc' | null;

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

type Position = 'All' | 'Forward' | 'Defense' | 'Goalie';

const skaterSortFields: SortField[] = ['name', 'number', 'gamesPlayed', 'goals', 'assists', 'points', 'ppg', 'pims'];
const goalieSortFields: SortField[] = ['name', 'number', 'gamesPlayed', 'wins', 'loses', 'so', 'ga', 'gaa', 'svPercent'];

const PlayerStats: React.FC = () => {
  const [playerData, setPlayerData] = useState<CombinedPlayerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'ppg', order: 'desc' });
  const [selectedPosition, setSelectedPosition] = useState<Position>('All');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isControlsOpen, setIsControlsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

        const [skaterStatsItems, goalieStatsItems, playerItems] = await Promise.all([
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('nomads_stats'),
            CACHE_KEYS.skaterStats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('nomads_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerInfo>(
            entriesUrl('nomads_players'),
            CACHE_KEYS.players,
            CACHE_TTL_MS
          ),
        ]);

        if (!isMounted) return;

        const playerMap: Map<string, PlayerInfo> = new Map(
          (playerItems ?? []).map((player: PlayerInfo) => [player.name, player])
        );

        const combinedData: CombinedPlayerData[] = [];

        const isGoaliePosition = (position: string | undefined) =>
          (position ?? '').split('/').some((pos) => pos.trim() === 'Goalie');

        // Skaters come from `nomads_stats`
        for (const stat of skaterStatsItems ?? []) {
          const playerInfo = playerMap.get(stat.player);
          const goalie = isGoaliePosition(playerInfo?.position);
          if (goalie) continue;

          combinedData.push({
            ...stat,
            position: playerInfo?.position ?? 'Forward',
            number: playerInfo?.number ?? 0,
          });
        }

        // Goalies come from `nomads_goalie_stats`
        for (const stat of goalieStatsItems ?? []) {
          const playerInfo = playerMap.get(stat.player);
          const goalie = isGoaliePosition(playerInfo?.position);
          if (!goalie) continue;

          combinedData.push({
            ...stat,
            position: playerInfo?.position ?? 'Goalie',
            number: playerInfo?.number ?? 0,
          });
        }

        // Normalize goalie derived fields for correct sorting/display.
        const enhancedData = combinedData.map((player: CombinedPlayerData) => {
          const isGoalie = player.position.split('/').some((pos) => pos.trim() === 'Goalie');
          if (!isGoalie) return player;

          const gp = Number(player.gamesPlayed) || 0;
          const ga = Number((player as any).ga ?? 0) || 0;
          const saves = Number((player as any).saves ?? 0) || 0;
          const shotsAgainst = Number((player as any).shotsAgainst ?? 0) || 0;

          const gaaComputed = gp > 0 ? ga / gp : 0;
          const svRatioComputed = saves + shotsAgainst > 0 ? saves / (saves + shotsAgainst) : 0;
          const svPercentComputed = svRatioComputed * 100;

          const normalizedSvPercent =
            player.svPercent !== undefined
              ? (() => {
                  const raw = Number(player.svPercent) || 0;
                  // If raw looks like a ratio (0-1), convert to percent.
                  return raw > 1 ? raw : raw * 100;
                })()
              : svPercentComputed;

          return {
            ...player,
            gaa: player.gaa ?? gaaComputed,
            svPercent: normalizedSvPercent,
          };
        });

        if (isMounted) {
          setPlayerData(enhancedData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading player data:', err);
        if (isMounted) {
          setError('Unable to load player statistics. Please try again later.');
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);


  const handleSort = useCallback((field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field 
        ? prevConfig.order === 'desc' 
          ? 'asc' 
          : prevConfig.order === 'asc' 
            ? null 
            : 'desc'
        : 'desc'
    }));
  }, []);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortConfig.field !== field) return '○';
    if (sortConfig.order === 'desc') return '▼';
    if (sortConfig.order === 'asc') return '▲';
    return '○';
  }, [sortConfig]);

  const handlePositionFilter = useCallback((position: Position) => {
    setSelectedPosition(position);
  }, []);

  useEffect(() => {
    // When switching to goalie mode, ensure sort field matches goalie stats.
    setSortConfig((prev) => {
      const allowed = selectedPosition === 'Goalie' ? goalieSortFields : skaterSortFields;
      return allowed.includes(prev.field) ? prev : { field: selectedPosition === 'Goalie' ? 'gaa' : 'ppg', order: 'desc' };
    });
  }, [selectedPosition]);

  const matchesPosition = useCallback((playerPosition: string, filterPosition: Position): boolean => {
    if (filterPosition === 'All') return true;
    return playerPosition.split('/').some(pos => pos.trim() === filterPosition);
  }, []);

  const sortedAndFilteredPlayers = useMemo(() => {
    let result = playerData.filter(player =>
      player.player.toLowerCase().includes(searchTerm.toLowerCase()) &&
      matchesPosition(player.position, selectedPosition)
    );

    if (sortConfig.order) {
      result = [...result].sort((a, b) => {
        if (sortConfig.field === 'name') {
          return sortConfig.order === 'asc'
            ? a.player.localeCompare(b.player)
            : b.player.localeCompare(a.player);
        }
        
        if (sortConfig.field === 'number') {
          const aValue = Number((a as any).number ?? 0);
          const bValue = Number((b as any).number ?? 0);
          return sortConfig.order === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aValue = Number((a as any)[sortConfig.field] ?? 0);
        const bValue = Number((b as any)[sortConfig.field] ?? 0);

        return sortConfig.order === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    return result;
  }, [playerData, searchTerm, sortConfig, selectedPosition, matchesPosition]);

  if (loading) {
    return (
      <StatsContainer>
        <LoadingState>Loading player statistics...</LoadingState>
      </StatsContainer>
    );
  }

  if (error) {
    return (
      <StatsContainer>
        <ErrorState>{error}</ErrorState>
      </StatsContainer>
    );
  }

  return (
    <StatsContainer>
      <PageTitle>Player Statistics</PageTitle>
      
      <ControlsSection isOpen={isControlsOpen}>
        <ControlsToggle 
          onClick={() => setIsControlsOpen(!isControlsOpen)}
          isOpen={isControlsOpen}
        >
          Filter & Sort
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </ControlsToggle>

        <ControlsPanel isOpen={isControlsOpen}>
          <SearchSection>
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </SearchSection>

          <FilterControls isOpen={isControlsOpen}>
            <FilterButton 
              isActive={selectedPosition === 'All'}
              onClick={() => {
                handlePositionFilter('All');
              }}
            >
              All Players
            </FilterButton>
            <FilterButton 
              isActive={selectedPosition === 'Forward'}
              onClick={() => {
                handlePositionFilter('Forward');
              }}
            >
              Forwards
            </FilterButton>
            <FilterButton 
              isActive={selectedPosition === 'Defense'}
              onClick={() => {
                handlePositionFilter('Defense');
              }}
            >
              Defense
            </FilterButton>
            <FilterButton 
              isActive={selectedPosition === 'Goalie'}
              onClick={() => {
                handlePositionFilter('Goalie');
              }}
            >
              Goalies
            </FilterButton>
          </FilterControls>

          <SortControls>
            {selectedPosition === 'Goalie' ? (
              <>
                <SortButton isActive={sortConfig.field === 'name'} onClick={() => handleSort('name')}>
                  Name {getSortIcon('name')}
                </SortButton>
                <SortButton
                  isActive={sortConfig.field === 'number'}
                  onClick={() => handleSort('number')}
                >
                  Number {getSortIcon('number')}
                </SortButton>
                <SortButton
                  isActive={sortConfig.field === 'gamesPlayed'}
                  onClick={() => handleSort('gamesPlayed')}
                >
                  Games {getSortIcon('gamesPlayed')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'wins'} onClick={() => handleSort('wins')}>
                  Wins {getSortIcon('wins')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'loses'} onClick={() => handleSort('loses')}>
                  Loses {getSortIcon('loses')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'so'} onClick={() => handleSort('so')}>
                  SO {getSortIcon('so')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'ga'} onClick={() => handleSort('ga')}>
                  GA {getSortIcon('ga')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'gaa'} onClick={() => handleSort('gaa')}>
                  GAA {getSortIcon('gaa')}
                </SortButton>
                <SortButton
                  isActive={sortConfig.field === 'svPercent'}
                  onClick={() => handleSort('svPercent')}
                >
                  SV% {getSortIcon('svPercent')}
                </SortButton>
              </>
            ) : (
              <>
                <SortButton isActive={sortConfig.field === 'name'} onClick={() => handleSort('name')}>
                  Name {getSortIcon('name')}
                </SortButton>
                <SortButton
                  isActive={sortConfig.field === 'number'}
                  onClick={() => handleSort('number')}
                >
                  Number {getSortIcon('number')}
                </SortButton>
                <SortButton
                  isActive={sortConfig.field === 'gamesPlayed'}
                  onClick={() => handleSort('gamesPlayed')}
                >
                  Games {getSortIcon('gamesPlayed')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'goals'} onClick={() => handleSort('goals')}>
                  Goals {getSortIcon('goals')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'assists'} onClick={() => handleSort('assists')}>
                  Assists {getSortIcon('assists')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'points'} onClick={() => handleSort('points')}>
                  Points {getSortIcon('points')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'ppg'} onClick={() => handleSort('ppg')}>
                  PPG {getSortIcon('ppg')}
                </SortButton>
                <SortButton isActive={sortConfig.field === 'pims'} onClick={() => handleSort('pims')}>
                  PIMS {getSortIcon('pims')}
                </SortButton>
              </>
            )}
          </SortControls>
        </ControlsPanel>
      </ControlsSection>

      <StatsGrid>
        <StatsHeader variant={selectedPosition === 'Goalie' ? 'goalie' : 'skater'}>
          <div className="header-cell">Player</div>
          <div className="header-cell">GP</div>
          {selectedPosition === 'Goalie' ? (
            <>
              <div className="header-cell">W</div>
              <div className="header-cell">L</div>
              <div className="header-cell">SO</div>
              <div className="header-cell">GA</div>
              <div className="header-cell">GAA</div>
              <div className="header-cell">SV%</div>
            </>
          ) : (
            <>
              <div className="header-cell">G</div>
              <div className="header-cell">A</div>
              <div className="header-cell">PTS</div>
              <div className="header-cell">PPG</div>
              <div className="header-cell">PIM</div>
            </>
          )}
        </StatsHeader>
        
        {sortedAndFilteredPlayers.length === 0 ? (
          <ErrorState>No players found matching your search criteria.</ErrorState>
        ) : (
          sortedAndFilteredPlayers.map(player => (
            <TigersPlayerCard player={player} isMobile={isMobile}  />
          ))
        )}
      </StatsGrid>
      
      <p style={{ textAlign: 'center', color: 'var(--gray)', marginTop: '2rem' }}>
        * Statistics are updated after each game
      </p>
    </StatsContainer>
  );
};

export default PlayerStats; 